import { useEffect, useRef, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { Button } from '@material-ui/core';
import { chain, noop } from 'lodash';
import { useLocation } from 'react-router-dom';
import VoipTestNowDetailPanel from '../../panels/voip-test-now-panels/VoipTestNowDetailPanel';
import VoipTestNowTestListPanel from '../../panels/voip-test-now-panels/VoipTestNowTestsPanel';
import { Page, PageHeader, PanelContainer, Actions } from '../../page-framework';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import useUiAlerts, { createAlertConfig, ERROR } from '../../../stores/AlertsStore';
import { useSuccessfulAction } from '../../../utilities/FormUtilities';
import { buildActionParams, collectDeleteErrors } from '../../pages-admin/agents/AgentListHelper';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import { NetTestNowIsLoadingProvider } from '../net-test-now-page/NetTestNowIsLoadingContext';

export const pageId = 'quickTests/voip';

const ListPanelActions = ({ setCreating, setReRun }) => {
    return (
        <Button
            color="primary"
            variant="contained"
            size="medium"
            onClick={() => {
                setCreating(true);
                setReRun(false);
            }}
        >
            <Trans>Create</Trans>
        </Button>
    );
};

const DuplicateDetailsPanelActions = ({ setCreating, setReRun, disabled }) => {
    return (
        <Button
            size="medium"
            disabled={disabled}
            onClick={() => {
                setCreating(true);
                setReRun(true);
            }}
        >
            <Trans>Re-Run</Trans>
        </Button>
    );
};

const RunTestActions = ({ submitRef }) => {
    return (
        <Button
            size="medium"
            color="primary"
            variant="contained"
            onClick={() => {
                submitRef.current.click();
            }}
        >
            <Trans>Run</Trans>
        </Button>
    );
};

const dataSource = {
    url: '/ipm/v1/admin/quicktests',
    params: {
        entityFields: 'name,tags',
        renderAsEntity: 'true',
    },
};

export default function VoipTestNowPage() {
    const location = useLocation();
    const submitRef = useRef();
    const [isDetailsPanel, setIsDetailsPanel] = useState(location?.state?.isDetailsPanel || false);
    const [deleteDisabled, setDeleteDisabled] = useState(false);
    const [reRunDisabled, setReRunDisabled] = useState(false);
    const [tableState, setTableState] = useState(null);
    const [tableSelection, setTableSelection] = useState({ selection: [location?.state?.testSelection] } || null);
    const handleSuccessfulDelete = useSuccessfulAction();
    const { deleteWrapper } = useFormDeleteWrapper();
    const formDeleteService = useFormDeleteService();
    const [, uiAlertActions] = useUiAlerts();
    const [reRun, setReRun] = useState(location?.state?.isDetailsPanel || false);
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger({
        interval: 20000,
    });

    const firstBreadcrumb = [{ text: t`VoIP Test Now` }];
    const [breadcrumbs, setBreadcrumbs] = useState(firstBreadcrumb);

    useEffect(() => {
        if (!location?.state?.isDetailsPanel) {
            setIsDetailsPanel(false);
        }
    }, [location]);

    const postDeleteHandler = (response) => {
        const { results } = response;
        const deleteErrors = chain(results).filter({ deleted: false }).map('error').map('details').value();
        if (!deleteErrors.length) {
            return handleSuccessfulDelete(response, null, refresher);
        }
        uiAlertActions.createAlert(createAlertConfig(ERROR, collectDeleteErrors(deleteErrors).join('  ')));
        refresher();
    };

    const handleConfirmedDelete = () => {
        return deleteWrapper({
            deleteFunction: () =>
                formDeleteService.deleteSelection(
                    `${dataSource.url}/selection/remove`,
                    buildActionParams(dataSource, tableState),
                    tableSelection
                ),
            refresherFunc: refresher,
            postDeleteHandler,
        })();
    };

    return (
        <Page id={pageId}>
            <NetTestNowIsLoadingProvider value={[false, noop]}>
                <PageHeader
                    title={t`VoIP Test Now`}
                    breadcrumbs={isDetailsPanel ? breadcrumbs : firstBreadcrumb}
                    actions={
                        <Actions>
                            {isDetailsPanel ? (
                                <>
                                    <RunTestActions submitRef={submitRef} />
                                </>
                            ) : (
                                <>
                                    <DuplicateDetailsPanelActions
                                        setCreating={setIsDetailsPanel}
                                        setReRun={setReRun}
                                        disabled={reRunDisabled}
                                    />
                                    <ConfirmDeleteButton
                                        disabled={deleteDisabled}
                                        handleConfirmedDelete={handleConfirmedDelete}
                                        plural={tableSelection?.selection?.length > 1}
                                    />
                                    <ListPanelActions setCreating={setIsDetailsPanel} setReRun={setReRun} />
                                </>
                            )}
                        </Actions>
                    }
                />
                <PanelContainer optimizeLayoutUpdates={false} flexHeight>
                    {isDetailsPanel ? (
                        <VoipTestNowDetailPanel
                            flexHeight
                            reRun={reRun}
                            tableSelection={tableSelection}
                            submitRef={submitRef}
                            setBreadcrumbs={setBreadcrumbs}
                        />
                    ) : (
                        <VoipTestNowTestListPanel
                            hideTitle
                            flexHeight
                            setTableState={setTableState}
                            setTableSelection={setTableSelection}
                            tableState={tableState}
                            tableSelection={tableSelection}
                            setDeleteDisabled={setDeleteDisabled}
                            setReRunDisabled={setReRunDisabled}
                            triggerRef={triggerRef}
                        />
                    )}
                </PanelContainer>
            </NetTestNowIsLoadingProvider>
        </Page>
    );
}
